import {
  FileDown,
  FileSpreadsheet,
  FileText,
  Plus,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import { sanitizeUserMessageForDisplay } from '../../constants/canvasRules';
import { useOrganization } from '../../contexts';
import { organizationService, toolService } from '../../services';
import '../../styles/markdown.css';
import {
  CanvasSessionPersistContext,
  Message,
} from '../../types/conversation';
import { putExecuteAiCanvasSessionState } from '../../services/ExecuteAiCanvasSessionStateService';
import {
  detectDownloadKind,
  resolveArtifactBase64,
  resolveArtifactDownloadUrl,
  shouldBlockTruncatedCompleteDownload,
} from '../../utils/analysisArtifacts';
import { downloadAnalysisArtifact } from '../../utils/downloadAnalysisArtifact';
import { Button, Input, Modal } from '../ui';
import AiThinkingIndicator from './AiThinkingIndicator';
import { AnalysisArtifactsBar } from './AnalysisArtifactsBar';
import { ArtifactDownloadStatus } from './ArtifactDownloadStatus';
import { CodeBlock } from './CodeBlock';
import DevSpottSymbolLoader from './DevSpottSymbolLoader';
import { MermaidBlock } from './MermaidBlock';

interface MessageBubbleProps {
  message: Message;
  formatTime: (date: Date | string) => string;
  /** Última mensagem em modo canvas: iframe ocupa a altura restante do chat até ao input. */
  fillCanvasHeight?: boolean;
  /** Se definido, debounce PUT dos campos do canvas para o backend (mesmo session_id do execute-ai). */
  canvasSessionPersist?: CanvasSessionPersistContext | null;
}

type HtmlRendererProps = {
  html: string;
  messageId: string;
  analysisArtifacts?: Message['analysisArtifacts'];
  onFieldValuesChange?: (values: Record<string, unknown>) => void;
  onTemplateContractChange?: (hasContract: boolean) => void;
  onTemplateStateSnapshot?: (state: Record<string, unknown> | null) => void;
  onContentHeightChange?: (height: number) => void;
  onCanvasAction?: (action: CanvasActionMessage) => void;
  contentHeight?: number;
  /** Preenche a altura do iframe até ao fundo da área de chat (última mensagem canvas). */
  fillAvailableHeight?: boolean;
};

type CanvasActionMessage = {
  action: 'print' | 'copy' | 'download' | 'open_link' | 'email';
  messageId: string;
  text?: string;
  href?: string;
  filename?: string;
  base64?: string;
  mimeType?: string;
};

const printHtmlWithoutPopup = (html: string, onError: () => void): void => {
  if (!html || !html.trim()) {
    onError();
    return;
  }

  const printFrame = document.createElement('iframe');
  printFrame.style.position = 'fixed';
  printFrame.style.right = '0';
  printFrame.style.bottom = '0';
  printFrame.style.width = '0';
  printFrame.style.height = '0';
  printFrame.style.border = '0';
  printFrame.style.opacity = '0';
  printFrame.setAttribute('aria-hidden', 'true');

  const cleanup = () => {
    if (printFrame.parentNode) {
      printFrame.parentNode.removeChild(printFrame);
    }
  };

  document.body.appendChild(printFrame);

  const doc = printFrame.contentDocument;
  if (!doc) {
    cleanup();
    onError();
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  printFrame.onload = () => {
    const frameWindow = printFrame.contentWindow;
    if (!frameWindow) {
      cleanup();
      onError();
      return;
    }

    const fallbackCleanupTimeout = window.setTimeout(cleanup, 2000);
    frameWindow.addEventListener(
      'afterprint',
      () => {
        window.clearTimeout(fallbackCleanupTimeout);
        cleanup();
      },
      { once: true }
    );

    frameWindow.focus();
    window.setTimeout(() => {
      try {
        frameWindow.print();
      } catch {
        window.clearTimeout(fallbackCleanupTimeout);
        cleanup();
        onError();
      }
    }, 80);
  };
};

const injectAnalysisArtifactsIntoCanvasHtml = (
  html: string,
  analysisArtifacts?: Message['analysisArtifacts']
): string => {
  if (!html || !analysisArtifacts) {
    return html;
  }

  const datasetScriptRegex =
    /<script[^>]+id\s*=\s*["']devspott-analysis-dataset["'][^>]*>\s*([\s\S]*?)\s*<\/script>/i;
  const match = datasetScriptRegex.exec(html);
  if (!match) {
    return html;
  }

  let dataset: Record<string, unknown>;
  try {
    const parsed = JSON.parse(match[1]);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return html;
    }
    dataset = { ...(parsed as Record<string, unknown>) };
  } catch {
    return html;
  }

  let changed = false;
  const assignIfMissing = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (
      dataset[key] === undefined ||
      dataset[key] === null ||
      String(dataset[key] || '').trim() === ''
    ) {
      dataset[key] = value;
      changed = true;
    }
  };

  assignIfMissing('xlsx_download_url', analysisArtifacts.xlsx_download_url);
  assignIfMissing('csv_download_url', analysisArtifacts.csv_download_url);
  assignIfMissing('pdf_download_url', analysisArtifacts.pdf_download_url);
  assignIfMissing('docx_download_url', analysisArtifacts.docx_download_url);
  assignIfMissing('pptx_download_url', analysisArtifacts.pptx_download_url);
  assignIfMissing('xlsx_base64', analysisArtifacts.xlsx_base64);
  assignIfMissing('csv_base64', analysisArtifacts.csv_base64);
  assignIfMissing('pdf_base64', analysisArtifacts.pdf_base64);
  assignIfMissing('docx_base64', analysisArtifacts.docx_base64);
  assignIfMissing('pptx_base64', analysisArtifacts.pptx_base64);
  assignIfMissing('xlsx_artifact_id', analysisArtifacts.xlsx_artifact_id);
  assignIfMissing('csv_artifact_id', analysisArtifacts.csv_artifact_id);
  assignIfMissing('pdf_artifact_id', analysisArtifacts.pdf_artifact_id);
  assignIfMissing('docx_artifact_id', analysisArtifacts.docx_artifact_id);
  assignIfMissing('pptx_artifact_id', analysisArtifacts.pptx_artifact_id);
  assignIfMissing('xlsx_file_name', analysisArtifacts.xlsx_file_name);
  assignIfMissing('csv_file_name', analysisArtifacts.csv_file_name);
  assignIfMissing('pdf_file_name', analysisArtifacts.pdf_file_name);
  assignIfMissing('docx_file_name', analysisArtifacts.docx_file_name);
  assignIfMissing('pptx_file_name', analysisArtifacts.pptx_file_name);
  assignIfMissing('xlsx_mime_type', analysisArtifacts.xlsx_mime_type);
  assignIfMissing('csv_mime_type', analysisArtifacts.csv_mime_type);
  assignIfMissing('pdf_mime_type', analysisArtifacts.pdf_mime_type);
  assignIfMissing('docx_mime_type', analysisArtifacts.docx_mime_type);
  assignIfMissing('pptx_mime_type', analysisArtifacts.pptx_mime_type);
  assignIfMissing('preview_rows', analysisArtifacts.preview_rows);
  assignIfMissing('total_rows', analysisArtifacts.total_rows);
  if (analysisArtifacts.truncated === true && dataset.truncated !== true) {
    dataset.truncated = true;
    changed = true;
  }

  if (!changed) {
    return html;
  }

  const replacementJson = JSON.stringify(dataset);
  const jsonStart = match.index + match[0].indexOf(match[1]);
  const jsonEnd = jsonStart + match[1].length;
  return html.slice(0, jsonStart) + replacementJson + html.slice(jsonEnd);
};

const buildCanvasDocument = (
  html: string,
  messageId: string,
  analysisArtifacts?: Message['analysisArtifacts']
): string => {
  const htmlWithArtifacts = injectAnalysisArtifactsIntoCanvasHtml(
    html,
    analysisArtifacts
  );
  const bridgeScript = `
    <script data-devspott-canvas-bridge="1">
      (function () {
        var CHANNEL_SOURCE = 'devspott-canvas';
        var CHANNEL_PARENT_SOURCE = 'devspott-canvas-parent';
        var MESSAGE_ID = ${JSON.stringify(messageId)};
        var INPUT_SELECTOR = '[data-field]';

        function normalizeValue(el) {
          var tag = (el.tagName || '').toLowerCase();
          var type = (el.getAttribute('type') || '').toLowerCase();

          if (tag === 'input' && type === 'checkbox') {
            return !!el.checked;
          }

          if (tag === 'input' && type === 'radio') {
            if (!el.checked) return undefined;
            return el.value;
          }

          if (tag === 'input' || tag === 'textarea' || tag === 'select') {
            return el.value;
          }

          if (el.hasAttribute('data-value')) {
            return el.getAttribute('data-value');
          }

          return (el.textContent || '').trim();
        }

        function collectValues() {
          var values = {};
          var nodes = document.querySelectorAll(INPUT_SELECTOR);

          nodes.forEach(function (el) {
            var fieldName = el.getAttribute('data-field');

            if (!fieldName) return;

            var value = normalizeValue(el);
            if (typeof value === 'undefined') return;
            values[fieldName] = value;
          });

          return values;
        }

        function buildDomSnapshot() {
          var sourceRoot = document.documentElement;
          if (!sourceRoot) {
            return '';
          }

          var cloneRoot = sourceRoot.cloneNode(true);
          var sourceControls = sourceRoot.querySelectorAll('input, textarea, select');
          var cloneControls = cloneRoot.querySelectorAll('input, textarea, select');

          for (var i = 0; i < sourceControls.length; i += 1) {
            var sourceControl = sourceControls[i];
            var cloneControl = cloneControls[i];
            if (!cloneControl) {
              continue;
            }

            var tag = (sourceControl.tagName || '').toLowerCase();
            var inputType = (sourceControl.getAttribute('type') || '').toLowerCase();

            if (tag === 'input') {
              if (inputType === 'checkbox' || inputType === 'radio') {
                if (sourceControl.checked) {
                  cloneControl.setAttribute('checked', 'checked');
                } else {
                  cloneControl.removeAttribute('checked');
                }
              } else {
                cloneControl.setAttribute('value', sourceControl.value || '');
              }
              continue;
            }

            if (tag === 'textarea') {
              cloneControl.textContent = sourceControl.value || '';
              continue;
            }

            if (tag === 'select') {
              var sourceOptions = sourceControl.options || [];
              var cloneOptions = cloneControl.options || [];
              for (var optionIndex = 0; optionIndex < cloneOptions.length; optionIndex += 1) {
                var sourceOption = sourceOptions[optionIndex];
                var cloneOption = cloneOptions[optionIndex];
                if (!sourceOption || !cloneOption) {
                  continue;
                }
                if (sourceOption.selected) {
                  cloneOption.setAttribute('selected', 'selected');
                } else {
                  cloneOption.removeAttribute('selected');
                }
              }
            }
          }

          var bridgeScripts = cloneRoot.querySelectorAll('script[data-devspott-canvas-bridge]');
          bridgeScripts.forEach(function (node) {
            if (node && node.parentNode) {
              node.parentNode.removeChild(node);
            }
          });

          return '<!DOCTYPE html>\\n' + cloneRoot.outerHTML;
        }

        function getTemplateState() {
          if (typeof window.__devspottGetState === 'function') {
            try {
              var state = window.__devspottGetState();
              if (state && typeof state === 'object') {
                return { has_contract: true, state: state };
              }
              return { has_contract: true, state: null };
            } catch (error) {
              return { has_contract: true, state: null };
            }
          }
          return { has_contract: false, state: null };
        }

        function setTemplateState(state) {
          var hasContract = typeof window.__devspottSetState === 'function';
          if (typeof window.__devspottSetState === 'function') {
            try {
              window.__devspottSetState(state || {});
              return { applied: true, has_contract: true };
            } catch (error) {
              return { applied: false, has_contract: true };
            }
          }
          return { applied: false, has_contract: hasContract };
        }

        function postValues() {
          window.parent.postMessage(
            {
              source: CHANNEL_SOURCE,
              type: 'fields_snapshot',
              messageId: MESSAGE_ID,
              values: collectValues(),
            },
            '*'
          );
        }

        function postAction(actionPayload) {
          window.parent.postMessage(
            Object.assign(
              {
                source: CHANNEL_SOURCE,
                type: 'canvas_action',
                messageId: MESSAGE_ID,
              },
              actionPayload
            ),
            '*'
          );
        }

        function normalizeDownloadPayload(payload) {
          if (!payload || typeof payload !== 'object') {
            return {};
          }

          var normalized = {};
          if (typeof payload.href === 'string') {
            normalized.href = payload.href;
          }
          if (typeof payload.filename === 'string') {
            normalized.filename = payload.filename;
          }
          if (typeof payload.text === 'string') {
            normalized.text = payload.text;
          }
          if (typeof payload.base64 === 'string') {
            normalized.base64 = payload.base64;
          }
          if (typeof payload.mimeType === 'string') {
            normalized.mimeType = payload.mimeType;
          } else if (typeof payload.mime_type === 'string') {
            normalized.mimeType = payload.mime_type;
          }
          return normalized;
        }

        function requestDownload(payload) {
          postAction(
            Object.assign(
              {
                action: 'download',
              },
              normalizeDownloadPayload(payload)
            )
          );
        }

        if (typeof window.__devspottRequestDownload !== 'function') {
          window.__devspottRequestDownload = requestDownload;
        }
        if (typeof window.__devspottDownloadFile !== 'function') {
          window.__devspottDownloadFile = requestDownload;
        }

        function postDomSnapshot() {
          window.parent.postMessage(
            {
              source: CHANNEL_SOURCE,
              type: 'dom_snapshot',
              messageId: MESSAGE_ID,
              html: buildDomSnapshot(),
            },
            '*'
          );
        }

        function postTemplateState() {
          var result = getTemplateState();
          window.parent.postMessage(
            {
              source: CHANNEL_SOURCE,
              type: 'template_state',
              messageId: MESSAGE_ID,
              has_contract: !!result.has_contract,
              state: result.state,
            },
            '*'
          );
        }

        function classifyUrlAction(url, explicitDownload) {
          var href = String(url || '').trim();
          if (!href || href === '#' || /^javascript:/i.test(href)) {
            return null;
          }

          if (/^mailto:/i.test(href)) {
            return { action: 'email', href: href };
          }

          var isDownload =
            !!explicitDownload ||
            /^data:application\\/pdf/i.test(href) ||
            /\\.pdf(\\?.*)?$/i.test(href) ||
            /(^|[\\/?=&_-])pdf([\\/?=&_.-]|$)/i.test(href) ||
            /^blob:/i.test(href);

          if (isDownload) {
            return { action: 'download', href: href };
          }

          return { action: 'open_link', href: href };
        }

        function postHeight() {
          var body = document.body;
          var doc = document.documentElement;
          var height = Math.max(
            body ? body.scrollHeight : 0,
            body ? body.offsetHeight : 0,
            doc ? doc.clientHeight : 0,
            doc ? doc.scrollHeight : 0,
            doc ? doc.offsetHeight : 0
          );

          window.parent.postMessage(
            {
              source: CHANNEL_SOURCE,
              type: 'content_height',
              messageId: MESSAGE_ID,
              height: height,
            },
            '*'
          );
        }

        window.addEventListener('load', function () {
          postValues();
          postHeight();
          setTimeout(postValues, 250);
          setTimeout(postHeight, 250);
          setTimeout(postHeight, 1000);
          setTimeout(postHeight, 2000);

          if (window.ResizeObserver) {
            var resizeObserver = new ResizeObserver(function () {
              postHeight();
            });
            resizeObserver.observe(document.body);
          }

          if (window.MutationObserver) {
            var mutationObserver = new MutationObserver(function () {
              postHeight();
            });
            mutationObserver.observe(document.body, {
              subtree: true,
              childList: true,
              attributes: true,
              characterData: true,
            });
          }
        });

        document.addEventListener('submit', function (event) {
          event.preventDefault();
        }, true);
        document.addEventListener('input', postValues, true);
        document.addEventListener('change', postValues, true);
        document.addEventListener('input', postHeight, true);
        document.addEventListener('change', postHeight, true);
        window.addEventListener('resize', postHeight);
        document.addEventListener(
          'click',
          function (event) {
            var target = event.target;
            if (!target || typeof target.closest !== 'function') {
              return;
            }
            var anchor = target.closest('a');
            if (!anchor) {
              return;
            }
            var href = anchor.getAttribute('href') || '';
            var downloadName = anchor.getAttribute('download') || undefined;
            var actionPayload = classifyUrlAction(
              href,
              anchor.hasAttribute('download')
            );
            if (!actionPayload) {
              return;
            }
            event.preventDefault();
            postAction(
              Object.assign({}, actionPayload, {
                filename: downloadName,
              })
            );
          },
          true
        );
        var originalPrint = window.print ? window.print.bind(window) : null;
        window.print = function () {
          postAction({ action: 'print' });
          return undefined;
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          var originalWriteText = navigator.clipboard.writeText.bind(navigator.clipboard);
          navigator.clipboard.writeText = function (text) {
            postAction({
              action: 'copy',
              text: text == null ? '' : String(text),
            });
            return Promise.resolve();
          };
        }
        document.addEventListener(
          'copy',
          function () {
            var selectedText = '';
            try {
              selectedText = String(window.getSelection ? window.getSelection() : '');
            } catch (e) {}
            if (selectedText) {
              postAction({ action: 'copy', text: selectedText });
            }
          },
          true
        );
        var originalWindowOpen = window.open ? window.open.bind(window) : null;
        window.open = function (url, target, features) {
          var actionPayload = classifyUrlAction(url, false);
          if (actionPayload) {
            postAction(actionPayload);
            return null;
          }
          if (originalWindowOpen) {
            return originalWindowOpen(url, target, features);
          }
          return null;
        };
        window.addEventListener('message', function (event) {
          var data = event && event.data ? event.data : {};
          if (
            data.source !== CHANNEL_PARENT_SOURCE ||
            data.messageId !== MESSAGE_ID
          ) {
            return;
          }

          if (data.type === 'request_dom_snapshot') {
            postDomSnapshot();
            return;
          }

          if (data.type === 'request_template_state') {
            postTemplateState();
            return;
          }

          if (data.type === 'apply_template_state') {
            var applyResult = setTemplateState(data.state || {});
            window.parent.postMessage(
              {
                source: CHANNEL_SOURCE,
                type: 'template_state_applied',
                messageId: MESSAGE_ID,
                applied: !!applyResult.applied,
                has_contract: !!applyResult.has_contract,
              },
              '*'
            );
            if (applyResult.applied) {
              postValues();
              postHeight();
            }
            return;
          }

          if (data.type === 'request_snapshot') {
            postValues();
          }
        });
      })();
    </script>
  `;

  if (/<\/body>/i.test(htmlWithArtifacts)) {
    return htmlWithArtifacts.replace(/<\/body>/i, `${bridgeScript}</body>`);
  }

  return `${htmlWithArtifacts}${bridgeScript}`;
};

const HtmlRenderer: React.FC<HtmlRendererProps> = ({
  html,
  messageId,
  analysisArtifacts,
  onFieldValuesChange,
  onTemplateContractChange,
  onTemplateStateSnapshot,
  onContentHeightChange,
  onCanvasAction,
  contentHeight = 400,
  fillAvailableHeight = false,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeDoc = useMemo(
    () => buildCanvasDocument(html || '', messageId, analysisArtifacts),
    [analysisArtifacts, html, messageId]
  );

  useEffect(() => {
    if (!iframeRef.current?.contentWindow) {
      return;
    }

    iframeRef.current.contentWindow.postMessage(
      {
        source: 'devspott-canvas-parent',
        type: 'request_snapshot',
        messageId,
      },
      '*'
    );
    iframeRef.current.contentWindow.postMessage(
      {
        source: 'devspott-canvas-parent',
        type: 'request_template_state',
        messageId,
      },
      '*'
    );
  }, [messageId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        !iframeRef.current ||
        event.source !== iframeRef.current.contentWindow
      ) {
        return;
      }

      const data = event.data as {
        source?: string;
        type?: string;
        messageId?: string;
        values?: Record<string, unknown>;
        height?: number;
        has_contract?: boolean;
        state?: unknown;
        action?: 'print' | 'copy' | 'download' | 'open_link' | 'email';
        text?: string;
        href?: string;
        filename?: string;
        base64?: string;
        mimeType?: string;
      };

      if (data?.source !== 'devspott-canvas' || data?.messageId !== messageId) {
        return;
      }

      if (
        data?.type === 'fields_snapshot' &&
        data.values &&
        typeof data.values === 'object'
      ) {
        onFieldValuesChange?.(data.values);
      }

      if (
        data?.type === 'content_height' &&
        typeof data.height === 'number' &&
        onContentHeightChange
      ) {
        onContentHeightChange(Math.max(400, data.height));
      }
      if (data?.type === 'template_state') {
        onTemplateContractChange?.(Boolean(data.has_contract));
        const st = data.state;
        if (st && typeof st === 'object' && !Array.isArray(st)) {
          onTemplateStateSnapshot?.(st as Record<string, unknown>);
        } else {
          onTemplateStateSnapshot?.(null);
        }
      }
      if (
        data?.type === 'canvas_action' &&
        data.action &&
        (data.action === 'print' ||
          data.action === 'copy' ||
          data.action === 'download' ||
          data.action === 'open_link' ||
          data.action === 'email')
      ) {
        onCanvasAction?.({
          action: data.action,
          messageId,
          text: data.text,
          href: data.href,
          filename: data.filename,
          base64: data.base64,
          mimeType: data.mimeType,
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    messageId,
    onCanvasAction,
    onContentHeightChange,
    onFieldValuesChange,
    onTemplateContractChange,
    onTemplateStateSnapshot,
  ]);

  const iframe = (
    <iframe
      ref={iframeRef}
      key={`iframe-${messageId}`}
      className={`w-full border-0 ${fillAvailableHeight ? 'min-h-[400px] flex-1' : ''}`}
      style={
        fillAvailableHeight
          ? {
              width: '100%',
              minHeight: 400,
              height: '100%',
              flex: '1 1 auto',
            }
          : {
              minHeight: '400px',
              width: '100%',
              height: `${contentHeight}px`,
            }
      }
      sandbox="allow-scripts allow-forms"
      srcDoc={iframeDoc}
      title="HTML Content"
      scrolling="no"
    />
  );

  if (fillAvailableHeight) {
    return (
      <div className="flex flex-1 min-h-0 w-full flex-col">{iframe}</div>
    );
  }

  return iframe;
};

const RUNTIME_ACTION_RE =
  /__devspottRuntimeAction\s*\(\s*["'](record\.(?:list|get|create|update|delete)|schema\.get)["']/gi;
const ENTITY_TYPE_RE = /entity_type\s*[:=]\s*["']([a-zA-Z_][a-zA-Z0-9_]*)["']/gi;
const DATA_ENTITY_TYPE_RE =
  /data-entity-type\s*=\s*["']([a-zA-Z_][a-zA-Z0-9_]*)["']/gi;
const DOMAIN_SCRIPT_RE =
  /<script[^>]*id=["']devspott-runtime-domain["'][^>]*>([\s\S]*?)<\/script>/i;

const DATA_RUNTIME_ACTION_ATTR_RE =
  /data-runtime-action\s*=\s*["'](record\.(?:list|get|create|update|delete)|schema\.get)["']/gi;

const inferRuntimeActionsFromHtml = (html: string): string[] => {
  const text = String(html || '');
  if (!text.trim()) {
    return [];
  }
  const seen = new Set<string>();
  RUNTIME_ACTION_RE.lastIndex = 0;
  let match = RUNTIME_ACTION_RE.exec(text);
  while (match) {
    const action = String(match[1] || '').trim().toLowerCase();
    if (action) {
      seen.add(action);
    }
    match = RUNTIME_ACTION_RE.exec(text);
  }
  DATA_RUNTIME_ACTION_ATTR_RE.lastIndex = 0;
  match = DATA_RUNTIME_ACTION_ATTR_RE.exec(text);
  while (match) {
    const action = String(match[1] || '').trim().toLowerCase();
    if (action) {
      seen.add(action);
    }
    match = DATA_RUNTIME_ACTION_ATTR_RE.exec(text);
  }
  return Array.from(seen);
};

const inferEntityTypesFromHtml = (html: string): string[] => {
  const text = String(html || '');
  if (!text.trim()) {
    return [];
  }
  const seen = new Set<string>();
  ENTITY_TYPE_RE.lastIndex = 0;
  DATA_ENTITY_TYPE_RE.lastIndex = 0;

  let match = ENTITY_TYPE_RE.exec(text);
  while (match) {
    const entityType = String(match[1] || '').trim();
    if (entityType) {
      seen.add(entityType);
    }
    match = ENTITY_TYPE_RE.exec(text);
  }

  match = DATA_ENTITY_TYPE_RE.exec(text);
  while (match) {
    const entityType = String(match[1] || '').trim();
    if (entityType) {
      seen.add(entityType);
    }
    match = DATA_ENTITY_TYPE_RE.exec(text);
  }

  const domainScriptMatch = text.match(DOMAIN_SCRIPT_RE);
  if (domainScriptMatch?.[1]) {
    try {
      const parsed = JSON.parse(domainScriptMatch[1]);
      const entities = Array.isArray(parsed?.entities) ? parsed.entities : [];
      entities.forEach((item: unknown) => {
        if (item && typeof item === 'object') {
          const entityType = String((item as { name?: unknown }).name || '').trim();
          if (entityType) {
            seen.add(entityType);
          }
        }
      });
    } catch {
      // Ignore malformed domain script when inferring schema.
    }
  }

  return Array.from(seen);
};

/** HTML que sugere estado persistido ou app com dados (blur até transformar em ferramenta). */
const canvasHtmlSuggestsPersistableTooling = (html: string): boolean => {
  const text = String(html || '');
  if (!text.trim()) {
    return false;
  }
  if (/\bdata-field\s*=/i.test(text)) {
    return true;
  }
  if (/\b__devspottGetState\b/i.test(text) || /\b__devspottSetState\b/i.test(text)) {
    return true;
  }
  if (/\bdevspott-runtime-domain\b/i.test(text)) {
    return true;
  }
  if (/\blocalStorage\b/i.test(text) || /\bsessionStorage\b/i.test(text)) {
    return true;
  }
  const actions = inferRuntimeActionsFromHtml(text);
  if (
    actions.some(a =>
      ['record.create', 'record.update', 'record.delete'].includes(a)
    )
  ) {
    return true;
  }
  return false;
};

const buildSchemaFromCanvasHtml = (html: string): Record<string, unknown> => {
  const entityTypes = inferEntityTypesFromHtml(html);
  const runtimeActions = inferRuntimeActionsFromHtml(html);

  const normalizedActions =
    runtimeActions.length > 0
      ? runtimeActions
      : entityTypes.length > 0
        ? ['record.list']
        : [];
  const hasMutationAction = normalizedActions.some(
    action => action === 'record.create' || action === 'record.update' || action === 'record.delete'
  );
  if (hasMutationAction && !normalizedActions.includes('record.list')) {
    normalizedActions.unshift('record.list');
  }
  const fallbackEntityType = entityTypes[0] || 'records';
  const indexedActions = normalizedActions.map(action => ({
    id: action,
    type: 'runtime_action',
    runtime_action: action,
    params: action.startsWith('record.')
      ? { entity_type: fallbackEntityType }
      : {},
  }));
  const actionMap =
    indexedActions.length > 0
      ? {
          version: '1.0',
          actions: indexedActions,
        }
      : {
          version: '1.0',
          actions: [
            {
              id: 'open.main',
              type: 'navigate',
              target: 'main',
            },
          ],
        };
  const componentTree = {
    root: {
      node_id: 'root',
      component: 'page',
      props: { title: 'Canvas Tool' },
      children: [],
    },
  };
  const runtimeContract = {
    init: { enabled: true },
    action: { enabled: true },
  };
  const shouldDeclareEntities =
    entityTypes.length > 0 || normalizedActions.some(action => action.startsWith('record.'));
  const baseEntityNames = shouldDeclareEntities
    ? entityTypes.length > 0
      ? entityTypes
      : [fallbackEntityType]
    : [];
  const entityNames = Array.from(new Set([...baseEntityNames, 'screen_state']));
  const entities = entityNames.map(entityType => ({
    name: entityType,
    required_fields: [],
    optional_fields: [],
    payload_schema: {
      required: [],
      properties: {},
    },
  }));

  return {
    version: '1.0',
    entities,
    component_tree: componentTree,
    action_map: actionMap,
    runtime_contract: runtimeContract,
    business_rules: {},
    presentation_spec: {
      version: '1.0',
      component_tree: componentTree,
      action_map: actionMap,
      runtime_contract: runtimeContract,
      business_rules: {},
    },
  };
};

const ARTIFACT_DOWNLOAD_HREF_RE =
  /^\/v1\/execute-ai\/analysis-artifacts\/[^/]+\/download(?:\?.*)?$/i;

const isArtifactDownloadHref = (href: string): boolean =>
  ARTIFACT_DOWNLOAD_HREF_RE.test(String(href || '').trim());

const inferArtifactFilenameFromLabel = (label: string): string => {
  const normalized = String(label || '').toLowerCase();
  if (normalized.includes('excel') || normalized.includes('planilha')) {
    return 'relatorio.xlsx';
  }
  if (normalized.includes('pdf')) {
    return 'relatorio.pdf';
  }
  if (normalized.includes('docx') || normalized.includes('word') || normalized.includes('documento')) {
    return 'relatorio.docx';
  }
  if (normalized.includes('pptx') || normalized.includes('ppt') || normalized.includes('slide')) {
    return 'relatorio.pptx';
  }
  if (normalized.includes('csv')) {
    return 'relatorio.csv';
  }
  return 'relatorio.bin';
};

const stripArtifactLineLeadingEmoji = (content: string): string => {
  const text = String(content || '');
  if (!text.trim()) {
    return text;
  }
  const artifactLinePattern =
    /^(\s*)(?:[📊📄📕🧾]+\s+)+(?=(?:\*\*)?\[[^\]]+\]\(\/v1\/execute-ai\/analysis-artifacts\/[^)]+\/download\))/gim;
  return text.replace(artifactLinePattern, '$1');
};

type ArtifactIconKind = 'xlsx' | 'pdf' | 'docx' | 'pptx' | 'generic';

const inferArtifactIconKind = (href: string, label: string): ArtifactIconKind => {
  const text = `${String(href || '')} ${String(label || '')}`.toLowerCase();
  if (text.includes('xlsx') || text.includes('excel') || text.includes('planilha')) {
    return 'xlsx';
  }
  if (text.includes('pdf')) {
    return 'pdf';
  }
  if (text.includes('docx') || text.includes('word') || text.includes('documento')) {
    return 'docx';
  }
  if (text.includes('pptx') || text.includes('ppt') || text.includes('slide')) {
    return 'pptx';
  }
  return 'generic';
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  formatTime,
  fillCanvasHeight = false,
  canvasSessionPersist = null,
}) => {
  const { t } = useTranslation();
  const isCanvasAssistant =
    message.role === 'assistant' && message.useCanvas === true;
  const canvasHtmlForLayout = isCanvasAssistant ? (message.content || '') : '';

  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const predefinedToolTypes = [
    'geral',
    'financeiro',
    'vendas',
    'rh',
    'operacoes',
  ];
  type SharedOrgTarget = {
    key: string;
    organizationId: number;
    organizationName: string;
  };
  const [showUpdatedEffect, setShowUpdatedEffect] = useState(false);
  const [showSaveToolModal, setShowSaveToolModal] = useState(false);
  const [toolName, setToolName] = useState('');
  const [toolTypes, setToolTypes] = useState<string[]>(predefinedToolTypes);
  const [toolType, setToolType] = useState('geral');
  const [newToolTypeInput, setNewToolTypeInput] = useState('');
  const [isAddingToolType, setIsAddingToolType] = useState(false);
  const [visibilityScope, setVisibilityScope] = useState<
    'private' | 'public' | 'specific'
  >('private');
  const [sharingKeyInput, setSharingKeyInput] = useState('');
  const [sharedTargets, setSharedTargets] = useState<SharedOrgTarget[]>([]);
  const [isResolvingSharingKey, setIsResolvingSharingKey] = useState(false);
  const [isSavingTool, setIsSavingTool] = useState(false);
  const [canvasContentToSave, setCanvasContentToSave] = useState('');
  const [canvasFieldValues, setCanvasFieldValues] = useState<
    Record<string, unknown>
  >({});
  const [canvasTemplateSnapshot, setCanvasTemplateSnapshot] = useState<
    Record<string, unknown> | null
  >(null);
  const [canvasHasTemplateContract, setCanvasHasTemplateContract] = useState<
    boolean | null
  >(null);
  const [canvasHeightByMessageId, setCanvasHeightByMessageId] = useState<
    Record<string, number>
  >({});

  const htmlSuggestsPersistableFromHtml = useMemo(
    () => canvasHtmlSuggestsPersistableTooling(canvasHtmlForLayout),
    [canvasHtmlForLayout]
  );

  useEffect(() => {
    if (!isCanvasAssistant) {
      return;
    }
    setCanvasHasTemplateContract(null);
    setCanvasTemplateSnapshot(null);
  }, [canvasHtmlForLayout, message.id, isCanvasAssistant]);

  useEffect(() => {
    if (
      !isCanvasAssistant ||
      !canvasSessionPersist ||
      message.isStreaming === true
    ) {
      return;
    }
    const persist = canvasSessionPersist;
    const delayMs = 900;
    const handle = window.setTimeout(() => {
      const payload: Record<string, unknown> = {
        v: 1,
        message_id: message.id,
        fields: canvasFieldValues,
      };
      if (canvasTemplateSnapshot && Object.keys(canvasTemplateSnapshot).length > 0) {
        payload.template_state = canvasTemplateSnapshot;
      }
      void putExecuteAiCanvasSessionState({
        crew_id: persist.crewId,
        session_id: persist.sessionId,
        payload,
        tool_type: 'execute_ai_canvas',
      }).catch(() => {
        /* evita spam de toast em debounce; erros aparecem na rede */
      });
    }, delayMs);
    return () => window.clearTimeout(handle);
  }, [
    isCanvasAssistant,
    canvasSessionPersist,
    message.isStreaming,
    message.id,
    canvasFieldValues,
    canvasTemplateSnapshot,
  ]);

  const showCanvasPersistBlur = useMemo(
    () =>
      isCanvasAssistant &&
      (htmlSuggestsPersistableFromHtml || canvasHasTemplateContract === true),
    [
      isCanvasAssistant,
      htmlSuggestsPersistableFromHtml,
      canvasHasTemplateContract,
    ]
  );

  useEffect(() => {
    if (message.role !== 'assistant' || !message.streamUpdatedAt) {
      return;
    }

    setShowUpdatedEffect(true);
    const timeoutId = setTimeout(() => setShowUpdatedEffect(false), 900);
    return () => clearTimeout(timeoutId);
  }, [message.role, message.streamUpdatedAt]);

  const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object') {
      const maybeAxios = error as {
        response?: { data?: { detail?: string; message?: string } };
        message?: string;
      };
      const detail = maybeAxios.response?.data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
      const messageFromApi = maybeAxios.response?.data?.message;
      if (typeof messageFromApi === 'string' && messageFromApi.trim()) {
        return messageFromApi;
      }
      if (typeof maybeAxios.message === 'string' && maybeAxios.message.trim()) {
        return maybeAxios.message;
      }
    }
    return t('tools.saveError');
  };

  const handleOpenSaveToolModal = (htmlContent: string) => {
    setCanvasContentToSave(htmlContent);
    setToolName(t('tools.defaultName'));
    setToolTypes(predefinedToolTypes);
    setToolType('geral');
    setNewToolTypeInput('');
    setIsAddingToolType(false);
    setVisibilityScope('private');
    setSharingKeyInput('');
    setSharedTargets([]);
    setCanvasHasTemplateContract(null);
    setShowSaveToolModal(true);
  };

  const handleCloseSaveToolModal = () => {
    setShowSaveToolModal(false);
    setCanvasContentToSave('');
    setToolName('');
    setCanvasFieldValues({});
    setToolTypes(predefinedToolTypes);
    setToolType('geral');
    setNewToolTypeInput('');
    setIsAddingToolType(false);
    setVisibilityScope('private');
    setSharingKeyInput('');
    setSharedTargets([]);
    setCanvasHasTemplateContract(null);
  };

  const handleCanvasFieldValuesChange = useCallback(
    (values: Record<string, unknown>) => {
      setCanvasFieldValues(values);
    },
    []
  );

  const handleAddToolType = () => {
    const normalized = newToolTypeInput.trim().toLowerCase();
    if (!normalized) {
      return;
    }

    setToolTypes(previous => {
      if (previous.includes(normalized)) {
        return previous;
      }
      return [...previous, normalized];
    });
    setToolType(normalized);
    setNewToolTypeInput('');
    setIsAddingToolType(false);
  };

  const handleAddSharingKey = async () => {
    const key = sharingKeyInput.trim().toUpperCase();
    if (!key) {
      toast.error(t('tools.sharingKeyRequired'));
      return;
    }
    if (sharedTargets.some(item => item.key === key)) {
      toast.error(t('tools.sharingKeyDuplicate'));
      return;
    }

    setIsResolvingSharingKey(true);
    try {
      const resolution = await organizationService.resolveSharingKeys([key]);
      if (
        resolution.invalid_keys.length > 0 ||
        resolution.resolved.length === 0
      ) {
        toast.error(t('tools.sharingKeyInvalid'));
        return;
      }

      const resolved = resolution.resolved[0];
      if (resolved.organization_id === currentOrganization?.id) {
        toast.error(t('tools.sharingKeyOwnOrg'));
        return;
      }

      setSharedTargets(previous => [
        ...previous,
        {
          key: resolved.key,
          organizationId: resolved.organization_id,
          organizationName: resolved.organization_name,
        },
      ]);
      setSharingKeyInput('');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsResolvingSharingKey(false);
    }
  };

  const handleRemoveSharingKey = (key: string) => {
    setSharedTargets(previous => previous.filter(item => item.key !== key));
  };

  const handleConfirmSaveTool = async () => {
    const trimmedName = toolName.trim();
    if (!trimmedName) {
      toast.error(t('tools.nameRequired'));
      return;
    }
    if (!canvasContentToSave.trim()) {
      toast.error(t('tools.emptyCanvasError'));
      return;
    }
    if (canvasHasTemplateContract === false) {
      toast(
        'Contrato de estado será adicionado automaticamente ao salvar a ferramenta.'
      );
    }
    const finalToolType = toolType.trim().toLowerCase();
    if (!finalToolType) {
      toast.error(t('tools.typeRequired'));
      return;
    }
    if (visibilityScope === 'specific' && sharedTargets.length === 0) {
      toast.error(t('tools.sharingKeyAtLeastOne'));
      return;
    }

    setIsSavingTool(true);
    try {
      let sharedOrganizationIds: number[] = [];
      if (visibilityScope === 'specific') {
        const resolution = await organizationService.resolveSharingKeys(
          sharedTargets.map(item => item.key)
        );
        if (resolution.invalid_keys.length > 0) {
          toast.error(
            t('tools.sharingKeyInvalidList', {
              keys: resolution.invalid_keys.join(', '),
            })
          );
          return;
        }
        sharedOrganizationIds = resolution.resolved
          .map(item => item.organization_id)
          .filter(id => id !== currentOrganization?.id);
        if (sharedOrganizationIds.length === 0) {
          toast.error(t('tools.noValidSharedOrg'));
          return;
        }
      }

      const created = await toolService.create({
        name: trimmedName,
        tool_type: finalToolType,
        html_content: canvasContentToSave,
        // Front sends a best-effort schema; backend is canonical and completes gaps.
        schema: buildSchemaFromCanvasHtml(canvasContentToSave),
        public: visibilityScope === 'public',
        visibility_scope: visibilityScope,
        shared_organization_ids: sharedOrganizationIds,
        hidden: false,
      });

      toast.success(t('tools.saveSuccess'));
      handleCloseSaveToolModal();
      navigate(`/ferramentas/${created.id}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSavingTool(false);
    }
  };

  const handleCanvasAction = useCallback(
    (action: CanvasActionMessage, htmlContent: string) => {
      if (action.action === 'copy') {
        const textToCopy = String(action.text || '').trim();
        if (!textToCopy) {
          return;
        }
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => {
            toast.success(t('common.copied'));
          })
          .catch(() => {
            toast.error(t('tools.saveValuesError'));
          });
        return;
      }

      if (action.action === 'download') {
        const triggerBlobDownload = (blob: Blob, filename: string): void => {
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
        };

        const preferredFilename = String(action.filename || '').trim();
        const preferredMime = String(action.mimeType || '').trim();
        const isXlsxMime = /spreadsheet|excel|sheet/i.test(preferredMime);
        const isCsvMime = /csv/i.test(preferredMime);
        const isPdfMime = /pdf/i.test(preferredMime);
        const isDocxMime = /wordprocessingml|msword|officedocument\.word/i.test(
          preferredMime
        );
        const isPptxMime = /presentationml|powerpoint|officedocument\.presentation/i.test(
          preferredMime
        );
        const downloadKind = detectDownloadKind(
          preferredMime,
          preferredFilename,
          action.href
        );
        const analysisArtifacts = message.analysisArtifacts || null;
        const isTruncatedDataset = Boolean(
          analysisArtifacts?.truncated === true ||
          (typeof analysisArtifacts?.total_rows === 'number' &&
            typeof analysisArtifacts?.preview_rows === 'number' &&
            analysisArtifacts.total_rows > analysisArtifacts.preview_rows)
        );
        const defaultFilename = isCsvMime
          ? 'relatorio.csv'
          : isXlsxMime
            ? 'relatorio.xlsx'
            : isPdfMime
              ? 'relatorio.pdf'
              : isDocxMime
                ? 'relatorio.docx'
                : isPptxMime
                  ? 'relatorio.pptx'
              : 'arquivo.bin';

        const rawBase64 = resolveArtifactBase64(
          downloadKind,
          String(action.base64 || '').trim(),
          analysisArtifacts
        );
        const artifactDownloadUrl = resolveArtifactDownloadUrl(
          downloadKind,
          analysisArtifacts
        );
        const hasCompleteArtifact = Boolean(
          rawBase64 ||
          artifactDownloadUrl ||
          (typeof action.href === 'string' && action.href.trim())
        );
        if (
          shouldBlockTruncatedCompleteDownload(
            downloadKind,
            isTruncatedDataset,
            hasCompleteArtifact
          )
        ) {
          toast.error(
            'Arquivo completo indisponível nesta resposta. Solicite a regeneração do relatório completo.'
          );
          return;
        }
        if (rawBase64) {
          try {
            const sanitizedBase64 = rawBase64
              .replace(/^data:[^;]+;base64,/i, '')
              .replace(/\s+/g, '');
            const binary = window.atob(sanitizedBase64);
            const bytes = new Uint8Array(binary.length);
            for (let index = 0; index < binary.length; index += 1) {
              bytes[index] = binary.charCodeAt(index);
            }
            triggerBlobDownload(
              new Blob([bytes], {
                type: preferredMime || 'application/octet-stream',
              }),
              preferredFilename || defaultFilename
            );
          } catch {
            toast.error(t('tools.saveValuesError'));
          }
          return;
        }
        if (artifactDownloadUrl) {
          void downloadAnalysisArtifact(
            artifactDownloadUrl,
            preferredFilename || defaultFilename
          ).catch(() => {
            toast.error(t('tools.saveValuesError'));
          });
          return;
        }

        const rawText = String(action.text || '');
        if (rawText) {
          const resolvedFilename =
            preferredFilename ||
            (rawText.includes(',') ? 'relatorio.csv' : defaultFilename);
          const resolvedMime =
            preferredMime ||
            (resolvedFilename.toLowerCase().endsWith('.csv')
              ? 'text/csv;charset=utf-8;'
              : resolvedFilename.toLowerCase().endsWith('.xlsx')
                ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                : resolvedFilename.toLowerCase().endsWith('.docx')
                  ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                  : resolvedFilename.toLowerCase().endsWith('.pptx')
                    ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                : 'text/plain;charset=utf-8;');
          triggerBlobDownload(
            new Blob([rawText], { type: resolvedMime }),
            resolvedFilename
          );
          return;
        }

        const href = String(action.href || '').trim();
        if (!href) {
          return;
        }
        if (/^javascript:/i.test(href)) {
          return;
        }
        if (href.startsWith('/')) {
          void downloadAnalysisArtifact(
            href,
            preferredFilename || defaultFilename
          ).catch(() => {
            toast.error(t('tools.saveValuesError'));
          });
          return;
        }
        const link = document.createElement('a');
        link.href = href;
        if (action.filename) {
          link.download = action.filename;
        } else if (
          /^data:/i.test(href) ||
          /^blob:/i.test(href) ||
          /pdf/i.test(href)
        ) {
          link.download = 'arquivo.pdf';
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      if (action.action === 'email') {
        const href = String(action.href || '').trim();
        if (!href || !/^mailto:/i.test(href)) {
          return;
        }
        window.location.href = href;
        return;
      }

      if (action.action === 'open_link') {
        const href = String(action.href || '').trim();
        if (!href || /^javascript:/i.test(href) || href === '#') {
          return;
        }
        const popup = window.open(href, '_blank', 'noopener,noreferrer');
        if (!popup) {
          window.location.assign(href);
        }
        return;
      }

      if (action.action === 'print') {
        printHtmlWithoutPopup(htmlContent || '', () => {
          toast.error(t('tools.saveValuesError'));
        });
      }
    },
    [message.analysisArtifacts, t]
  );

  if (message.role === 'status') {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-1.5 max-w-md relative overflow-hidden status-message-pulse">
        {/* Efeito de iluminação pulsante */}
        <div className="absolute inset-0 rounded-lg status-message-glow-layer"></div>
        <div className="relative z-10">
          <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  // Se for mensagem do assistente com useCanvas, renderizar sem o bubble padrão
  // IMPORTANTE: Esta verificação deve vir ANTES do return padrão para garantir que o HTML seja renderizado
  if (message.role === 'assistant' && message.useCanvas === true) {
    const htmlContent = message.content || '';
    const trimmedContent = htmlContent.trim();

    // Validação mais flexível do HTML
    // Verifica se começa com tags HTML comuns ou DOCTYPE (ignorando espaços em branco)
    const isValidHtml =
      trimmedContent.toLowerCase().startsWith('<!doctype') ||
      trimmedContent.toLowerCase().startsWith('<!doctype html') ||
      trimmedContent.toLowerCase().startsWith('<html') ||
      trimmedContent.toLowerCase().startsWith('<html ') ||
      trimmedContent.toLowerCase().startsWith('<html>') ||
      (trimmedContent.includes('<html') &&
        trimmedContent.includes('</html>')) ||
      (trimmedContent.includes('<!DOCTYPE') &&
        trimmedContent.includes('</html>'));

    // Se useCanvas é true, sempre tentar renderizar como HTML, mesmo se a validação falhar
    // O backend está enviando HTML quando use_canvas = true, então confiamos nisso
    if (!isValidHtml && trimmedContent.length > 0) {
      // Keep rendering to avoid losing UX; iframe uses strict sandbox + sanitization.
    }

    // Garantir que o HTML seja renderizado no iframe, não como texto
    const hasHtmlContent = Boolean(htmlContent && htmlContent.trim());
    // Mesmo efeito que quando não tem useCanvas: mostrar quando está streaming com status mas sem conteúdo
    const showPulseEffect =
      message.isStreaming &&
      message.assistantPhase === 'thinking' &&
      !hasHtmlContent;

    return (
      <div
        className={`w-full max-w-full relative ${
          fillCanvasHeight ? 'flex h-full min-h-0 flex-1 flex-col' : ''
        } ${
          showUpdatedEffect
            ? 'ring-2 ring-emerald-400/70 rounded-lg transition-all duration-300'
            : ''
        }`}
      >
        <div
          className={
            fillCanvasHeight
              ? 'flex min-h-0 min-w-0 flex-1 flex-row items-stretch gap-1.5'
              : 'flex min-w-0 items-start space-x-1.5'
          }
        >
          {hasHtmlContent ? (
            <span
              className={`inline-flex flex-shrink-0 ${
                fillCanvasHeight ? 'self-start pt-0.5' : 'mt-0.5'
              }`}
              role="img"
              aria-label="Assistente"
            >
              <DevSpottSymbolLoader size="xs" animated={false} />
            </span>
          ) : (
            <span
              className={`h-[18px] w-[18px] flex-shrink-0 ${
                fillCanvasHeight ? 'self-start pt-0.5' : 'mt-0.5'
              }`}
              aria-hidden
            />
          )}
          <div
            className={`relative flex min-w-0 flex-col ${
              hasHtmlContent
                ? 'bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden'
                : 'overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-900'
            } ${
              fillCanvasHeight
                ? 'h-full min-h-[400px] flex-1'
                : 'min-h-[400px] flex-1'
            }`}
          >
            {hasHtmlContent ? (
              <>
                <div
                  className={`relative flex w-full min-h-0 flex-col ${
                    fillCanvasHeight ? 'flex-1' : ''
                  }`}
                >
                  <HtmlRenderer
                    key={`html-${message.id}`}
                    html={htmlContent}
                    messageId={message.id}
                    analysisArtifacts={message.analysisArtifacts}
                    onFieldValuesChange={handleCanvasFieldValuesChange}
                    onTemplateContractChange={setCanvasHasTemplateContract}
                    onTemplateStateSnapshot={setCanvasTemplateSnapshot}
                    onCanvasAction={action =>
                      handleCanvasAction(action, htmlContent)
                    }
                    onContentHeightChange={
                      fillCanvasHeight
                        ? undefined
                        : height =>
                            setCanvasHeightByMessageId(previous => {
                              if (previous[message.id] === height) {
                                return previous;
                              }
                              return { ...previous, [message.id]: height };
                            })
                    }
                    contentHeight={canvasHeightByMessageId[message.id] ?? 400}
                    fillAvailableHeight={fillCanvasHeight}
                  />
                  {showCanvasPersistBlur ? (
                    <div
                      className="absolute inset-0 z-30 flex items-center justify-center rounded-lg bg-white/45 px-4 py-6 backdrop-blur-md dark:bg-gray-950/55 pointer-events-auto"
                      role="status"
                      aria-live="polite"
                    >
                      <p className="max-w-md text-center text-sm font-semibold text-gray-900 shadow-sm dark:text-gray-100">
                        {t('tools.canvasPersistBlurHint')}
                      </p>
                    </div>
                  ) : null}
                </div>
                {message.artifactsGenerating && (
                  <div className="flex-shrink-0 px-4 pb-4">
                    <ArtifactDownloadStatus
                      analysisArtifacts={message.analysisArtifacts}
                      isGenerating={Boolean(message.artifactsGenerating)}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="relative h-full min-h-[400px]">
                <AiThinkingIndicator
                  phase={
                    message.assistantPhase ??
                    (showPulseEffect ? 'thinking' : undefined)
                  }
                  status={message.currentStatus}
                  variant="canvas"
                />
              </div>
            )}

            {hasHtmlContent && (
              <div className="sticky bottom-3 z-40 -mt-14 pr-3 pb-3 flex justify-end pointer-events-none">
                <Button
                  size="md"
                  variant="warning"
                  onClick={() => handleOpenSaveToolModal(htmlContent)}
                  className="h-11 px-5 rounded-full text-sm canvas-transform-cta pointer-events-auto"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {t('tools.transformFromCanvas')}
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-start ml-5 mt-1 gap-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatTime(message.timestamp)}
          </p>
        </div>
        <Modal
          isOpen={showSaveToolModal}
          onClose={handleCloseSaveToolModal}
          title={t('tools.saveModalTitle')}
          size="md"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('tools.saveModalDescription')}
            </p>
            <Input
              value={toolName}
              onChange={event => setToolName(event.target.value)}
              placeholder={t('tools.namePlaceholder')}
              autoFocus
            />
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('tools.toolTypeLabel')}
              </label>
              <div className="flex flex-wrap gap-2">
                {toolTypes.map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setToolType(type)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      toolType === type
                        ? 'border-orange-500 bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'
                        : 'border-gray-300 text-gray-700 hover:border-orange-300 dark:border-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {type}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setIsAddingToolType(previous => !previous)}
                  className="rounded-full border border-gray-300 text-gray-700 hover:border-orange-300 dark:border-gray-700 dark:text-gray-300 px-3 py-1 text-xs font-medium transition-colors inline-flex items-center gap-1"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('tools.addTypeButton')}
                </button>
              </div>
              {isAddingToolType ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={newToolTypeInput}
                    onChange={event => setNewToolTypeInput(event.target.value)}
                    placeholder={t('tools.addTypePlaceholder')}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleAddToolType();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddToolType}
                    disabled={!newToolTypeInput.trim()}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('tools.addTypeButton')}
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Visibilidade
              </label>
              <select
                value={visibilityScope}
                onChange={event =>
                  setVisibilityScope(
                    event.target.value as 'private' | 'public' | 'specific'
                  )
                }
                className="input w-full"
              >
                <option value="public">{t('tools.visibilityPublic')}</option>
                <option value="private">{t('tools.visibilityPrivate')}</option>
                <option value="specific">
                  {t('tools.visibilitySpecific')}
                </option>
              </select>
            </div>
            {visibilityScope === 'specific' ? (
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {t('tools.sharingKeyLabel')}
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    value={sharingKeyInput}
                    onChange={event =>
                      setSharingKeyInput(event.target.value.toUpperCase())
                    }
                    placeholder={t('tools.sharingKeyPlaceholder')}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddSharingKey}
                    loading={isResolvingSharingKey}
                    disabled={!sharingKeyInput.trim()}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('tools.addTypeButton')}
                  </Button>
                </div>
                <div className="space-y-2">
                  {sharedTargets.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('tools.noSharingKeysAdded')}
                    </p>
                  ) : (
                    sharedTargets.map(item => (
                      <div
                        key={item.key}
                        className="flex items-center justify-between gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                            {item.organizationName}
                          </p>
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                            {item.key}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveSharingKey(item.key)}
                          className="text-gray-500 hover:text-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : null}
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleCloseSaveToolModal}
                disabled={isSavingTool}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleConfirmSaveTool} loading={isSavingTool}>
                {t('tools.saveButton')}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  // Verificar se deve mostrar efeito pulsante no balão
  const showThinkingInBubble =
    message.role === 'assistant' &&
    message.isStreaming &&
    message.assistantPhase === 'thinking' &&
    !message.content;

  const displayUserContent =
    message.role === 'user'
      ? sanitizeUserMessageForDisplay(message.content || '')
      : message.content;
  const displayAssistantContentWithoutArtifactEmoji =
    message.role === 'assistant'
      ? stripArtifactLineLeadingEmoji(message.content || '')
      : message.content;

  return (
    <div
      className={`w-[95%] max-w-full relative ${
        showThinkingInBubble
          ? 'rounded-lg overflow-hidden'
          : `px-3 py-1.5 rounded-lg ${
              message.role === 'user'
                ? 'bg-primary-600 dark:bg-gray-700 text-white dark:text-gray-100'
                : 'bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100'
            }`
      } ${
        showUpdatedEffect
          ? 'ring-2 ring-emerald-400/70 rounded-lg transition-all duration-300'
          : ''
      }`}
    >
      <div
        className={`flex items-start space-x-1.5 min-w-0 ${
          showThinkingInBubble
            ? 'rounded-lg bg-gray-100 dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100'
            : ''
        }`}
      >
        {message.role === 'assistant' &&
          (showThinkingInBubble ? (
            <span
              className="mt-0.5 h-[18px] w-[18px] flex-shrink-0"
              aria-hidden
            />
          ) : (
            <span
              className="inline-flex mt-0.5 flex-shrink-0"
              role="img"
              aria-label="Assistente"
            >
              <DevSpottSymbolLoader size="xs" animated={false} />
            </span>
          ))}
        {message.role === 'user' && (
          <User className="h-4 w-4 mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          {message.role === 'assistant' ? (
            <>
              {/* Thinking phase — no content yet */}
              {showThinkingInBubble ? (
                <AiThinkingIndicator
                  phase="thinking"
                  status={message.currentStatus}
                  variant="bubble"
                />
              ) : (
                <div className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-h1:text-xl prose-h1:mb-2 prose-h1:mt-0 prose-h1:text-gray-900 dark:prose-h1:text-gray-100 prose-h1:border-b prose-h1:border-gray-200 dark:prose-h1:border-gray-700 prose-h1:pb-2 prose-h2:text-lg prose-h2:mb-2 prose-h2:mt-3 prose-h2:text-gray-900 dark:prose-h2:text-gray-100 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-gray-700 prose-h2:pb-1 prose-h3:text-base prose-h3:mb-1 prose-h3:mt-2 prose-h3:text-gray-900 dark:prose-h3:text-gray-100 prose-p:my-1 prose-p:text-gray-800 dark:prose-p:text-gray-200 prose-p:leading-6 prose-ul:my-1 prose-ul:space-y-1 prose-ol:my-1 prose-ol:space-y-1 prose-li:my-0 prose-li:text-gray-800 dark:prose-li:text-gray-200 prose-table:my-2 prose-table:border-collapse prose-table:w-full prose-th:bg-gray-50 dark:prose-th:bg-gray-800 prose-th:px-3 prose-th:py-2 prose-th:border prose-th:border-gray-200 dark:prose-th:border-gray-700 prose-th:text-left prose-th:text-xs prose-th:font-semibold prose-th:text-gray-700 dark:prose-th:text-gray-300 prose-th:uppercase prose-th:tracking-wider prose-td:px-3 prose-td:py-2 prose-td:border prose-td:border-gray-200 dark:prose-td:border-gray-700 prose-td:text-sm prose-td:text-gray-900 dark:prose-td:text-gray-100 prose-strong:font-semibold prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono prose-code:text-gray-800 dark:prose-code:text-gray-200 prose-pre:bg-gray-50 dark:prose-pre:bg-gray-900 prose-pre:border prose-pre:border-gray-200 dark:prose-pre:border-gray-700 prose-pre:rounded-md prose-pre:p-3 prose-pre:overflow-x-auto prose-pre:my-2 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 dark:prose-blockquote:border-gray-600 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:my-2 prose-blockquote:bg-gray-50 dark:prose-blockquote:bg-gray-800/50 prose-blockquote:py-2 prose-blockquote:rounded-r-md prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline prose-hr:my-4 prose-hr:border-gray-200 dark:prose-hr:border-gray-700">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeHighlight]}
                    components={{
                      table: ({ children, ...props }) => (
                        <div className="overflow-x-auto my-2">
                          <table
                            className="min-w-full border-collapse border border-gray-200 dark:border-gray-700 rounded-md"
                            {...props}
                          >
                            {children}
                          </table>
                        </div>
                      ),
                      thead: ({ children, ...props }) => (
                        <thead
                          className="bg-gray-50 dark:bg-gray-800"
                          {...props}
                        >
                          {children}
                        </thead>
                      ),
                      tbody: ({ children, ...props }) => (
                        <tbody
                          className="divide-y divide-gray-200 dark:divide-gray-700"
                          {...props}
                        >
                          {children}
                        </tbody>
                      ),
                      tr: ({ children, ...props }) => (
                        <tr
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          {...props}
                        >
                          {children}
                        </tr>
                      ),
                      th: ({ children, ...props }) => (
                        <th
                          className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700"
                          {...props}
                        >
                          {children}
                        </th>
                      ),
                      td: ({ children, ...props }) => (
                        <td
                          className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700"
                          {...props}
                        >
                          {children}
                        </td>
                      ),
                      code: ({ children, className, ...props }) => {
                        const isInline = !className;
                        if (isInline) {
                          return (
                            <code
                              className="bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded text-xs font-mono border border-gray-300 dark:border-gray-700"
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        }
                        // Para blocos de código, o componente pre será renderizado
                        return (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      },
                      pre: ({ children, className, ...props }) => {
                        const codeElement = React.Children.toArray(
                          children
                        ).find(child => {
                          if (!React.isValidElement(child)) {
                            return false;
                          }
                          const maybeClass = String(
                            (child.props as { className?: unknown })?.className || ''
                          );
                          return (
                            child.type === 'code' ||
                            /language-|hljs/i.test(maybeClass)
                          );
                        }) as React.ReactElement | undefined;

                        if (codeElement) {
                          const codeClassName =
                            codeElement.props?.className || '';
                          const codeContent = String(
                            codeElement.props?.children ?? ''
                          ).trim();
                          const isMermaidByClass =
                            /language-mermaid/i.test(codeClassName);
                          const isMermaidByContent =
                            /^(graph\s+(td|lr|rl|bt)|flowchart\s+(td|lr|rl|bt)|sequencediagram|classdiagram|erdiagram|statediagram|journey|gantt|pie)\b/i.test(
                              codeContent
                            );

                          if (isMermaidByClass || isMermaidByContent) {
                            return (
                              <MermaidBlock
                                chart={codeContent}
                              />
                            );
                          }

                          return (
                            <CodeBlock
                              className={codeClassName}
                              preClassName={className || ''}
                            >
                              {codeElement.props?.children}
                            </CodeBlock>
                          );
                        }

                        return (
                          <pre
                            className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3 overflow-x-auto my-2"
                            {...props}
                          >
                            {children}
                          </pre>
                        );
                      },
                      blockquote: ({ children, ...props }) => (
                        <blockquote
                          className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-700 dark:text-gray-300 my-2 bg-gray-50 dark:bg-gray-800/50 py-2 rounded-r-md"
                          {...props}
                        >
                          {children}
                        </blockquote>
                      ),
                      ul: ({ children, ...props }) => (
                        <ul
                          className="list-disc list-inside space-y-1 my-1 pl-4"
                          {...props}
                        >
                          {children}
                        </ul>
                      ),
                      ol: ({ children, ...props }) => (
                        <ol
                          className="list-decimal list-inside space-y-1 my-1 pl-4"
                          {...props}
                        >
                          {children}
                        </ol>
                      ),
                      li: ({ children, ...props }) => (
                        <li
                          className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed"
                          {...props}
                        >
                          {children}
                        </li>
                      ),
                      h1: ({ children, ...props }) => (
                        <h1
                          className="text-xl font-semibold text-gray-900 dark:text-gray-100 my-2 mt-0 border-b border-gray-200 dark:border-gray-700 pb-2"
                          {...props}
                        >
                          {children}
                        </h1>
                      ),
                      h2: ({ children, ...props }) => (
                        <h2
                          className="text-lg font-semibold text-gray-900 dark:text-gray-100 my-2 mt-3 border-b border-gray-200 dark:border-gray-700 pb-1"
                          {...props}
                        >
                          {children}
                        </h2>
                      ),
                      h3: ({ children, ...props }) => (
                        <h3
                          className="text-base font-semibold text-gray-900 dark:text-gray-100 my-1 mt-2"
                          {...props}
                        >
                          {children}
                        </h3>
                      ),
                      p: ({ children, ...props }) => (
                        <p
                          className="text-sm text-gray-800 dark:text-gray-200 my-1 leading-6"
                          {...props}
                        >
                          {children}
                        </p>
                      ),
                      strong: ({ children, ...props }) => (
                        <strong
                          className="font-semibold text-gray-900 dark:text-gray-100"
                          {...props}
                        >
                          {children}
                        </strong>
                      ),
                      em: ({ children, ...props }) => (
                        <em
                          className="italic text-gray-700 dark:text-gray-300"
                          {...props}
                        >
                          {children}
                        </em>
                      ),
                      a: ({ children, ...props }) => (
                        (() => {
                          const href = String(props.href || '').trim();
                          const labelText = React.Children.toArray(children)
                            .map(child =>
                              typeof child === 'string'
                                ? child
                                : React.isValidElement(child) &&
                                    typeof child.props.children === 'string'
                                  ? child.props.children
                                  : ''
                            )
                            .join(' ')
                            .trim();
                          const isArtifactLink = isArtifactDownloadHref(href);
                          const artifactIconKind = inferArtifactIconKind(
                            href,
                            labelText
                          );

                          const handleLinkClick = (
                            event: React.MouseEvent<HTMLAnchorElement>
                          ) => {
                            if (!href || /^javascript:/i.test(href)) {
                              event.preventDefault();
                              return;
                            }
                            if (!isArtifactLink) {
                              return;
                            }
                            event.preventDefault();
                            const fallbackFilename =
                              inferArtifactFilenameFromLabel(labelText);
                            void downloadAnalysisArtifact(
                              href,
                              props.download
                                ? String(props.download)
                                : fallbackFilename
                            ).catch(() => {
                              toast.error(t('tools.saveValuesError'));
                            });
                          };

                          const defaultClassName = isArtifactLink
                            ? 'inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 no-underline shadow-sm transition-all hover:-translate-y-[1px] hover:border-blue-300 hover:bg-blue-100 dark:border-blue-800/70 dark:bg-blue-900/30 dark:text-blue-200 dark:hover:border-blue-700 dark:hover:bg-blue-900/45'
                            : 'text-blue-600 dark:text-blue-400 no-underline hover:underline font-medium';
                          const mergedClassName = props.className
                            ? `${defaultClassName} ${props.className}`
                            : defaultClassName;

                          return (
                            <a
                              {...props}
                              href={href}
                              className={mergedClassName}
                              onClick={handleLinkClick}
                            >
                              {isArtifactLink ? (
                                artifactIconKind === 'xlsx' ? (
                                  <FileSpreadsheet
                                    className="h-4 w-4 shrink-0"
                                    aria-hidden
                                  />
                                ) : artifactIconKind === 'pdf' ? (
                                  <FileText
                                    className="h-4 w-4 shrink-0"
                                    aria-hidden
                                  />
                                ) : artifactIconKind === 'docx' ? (
                                  <FileText
                                    className="h-4 w-4 shrink-0"
                                    aria-hidden
                                  />
                                ) : artifactIconKind === 'pptx' ? (
                                  <FileDown
                                    className="h-4 w-4 shrink-0"
                                    aria-hidden
                                  />
                                ) : (
                                  <FileDown
                                    className="h-4 w-4 shrink-0"
                                    aria-hidden
                                  />
                                )
                              ) : null}
                              {children}
                            </a>
                          );
                        })()
                      ),
                      hr: ({ ...props }) => (
                        <hr
                          className="my-4 border-gray-200 dark:border-gray-700"
                          {...props}
                        />
                      ),
                    }}
                  >
                    {displayAssistantContentWithoutArtifactEmoji}
                  </ReactMarkdown>
                  {/* Blinking caret while answering */}
                  {message.isStreaming &&
                    message.assistantPhase === 'answering' && (
                      <span className="ai-caret" aria-hidden="true" />
                    )}
                  {/* Download chips for analysis artefacts.
                      Only the canvas branch had visible download UI; chat
                      bubbles silently dropped the analysis_artifacts payload
                      and the user was left looking for a link they could
                      never click. The bar renders one chip per format
                      (xlsx/csv/pdf/json) when the SSE done event includes
                      the corresponding artifact_id/download_url. */}
                  <AnalysisArtifactsBar
                    analysisArtifacts={message.analysisArtifacts}
                  />
                  {message.imageAnalysis?.notice ? (
                    <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200">
                      {message.imageAnalysis.notice}
                    </div>
                  ) : null}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-2">
              {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {message.attachments.map(attachment => (
                    <img
                      key={attachment.id}
                      src={attachment.preview_url}
                      alt={attachment.file_name}
                      className="h-20 w-20 rounded-md border border-white/30 object-cover"
                    />
                  ))}
                </div>
              )}
              <p className="text-sm whitespace-pre-wrap leading-relaxed">
                {displayUserContent}
              </p>
            </div>
          )}
          <p
            className={`text-xs mt-0.5 ${
              message.role === 'user'
                ? 'text-primary-100 dark:text-gray-300'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {formatTime(message.timestamp)}
          </p>
        </div>
      </div>
    </div>
  );
};
